import React from "react";
import { Button, Row, Col, Form } from 'react-bootstrap';
import * as PSLib from "poolside-js-lib";
import Locale from "../../locale";
import ConfiguratorAddDevice from "./ConfiguratorAddDevice";
import getCompatibleAttendants from "./ConfiguratorLogic";
import { context } from '../../components/AppContext';
import externalConfiguration from "../../externalConfiguration";
import { FaPen, FaTrash, FaClone} from "react-icons/fa";

const WaterChemistryManagementOptions = {
    none: "none",
    sensing_and_or_dosing: "sensing_and_or_dosing",
};

function getWaterChemistryManagementOptionsLocalised(value) {
    switch (value) {
    case WaterChemistryManagementOptions.none: return Locale.gettext("None");
    case WaterChemistryManagementOptions.sensing_and_or_dosing: return Locale.gettext("Sensing and/or Dosing");
    default:
        return value;
    }
};

function showError(err) {
    let text = Locale.gettext("Unexpected error. Please reach out to our sales team at sales@poolside.tech or +1 (267) 810-0600");
    if (err?.msgForUser) {
        text = err.msgForUser;
    }
    if (context.userInfo?.admin) {
        const msgForDev = [];
        if (err?.msgForDev) {
            msgForDev.push(err.msgForDev);
        }
        const errString = err.toString && err.toString();
        if (errString) {
            msgForDev.push(errString);
        }

        if (msgForDev.length) {
            text += (" DEV: " + msgForDev.join(" "));
        }
    }
    return text;
}

const installationTypeOptions = {
    new_construction: "new_construction",
    retrofit: "retrofit",
};

function getInstallationTypeOptionsLocalised(value) {
    switch (value) {
    case installationTypeOptions.new_construction: return Locale.gettext("New Construction");
    case installationTypeOptions.retrofit: return Locale.gettext("Retrofit");
    default:
        return value;
    }
};

const marginTop = 20;

const style = {
    fieldset: {
        borderStyle: "solid", borderWidth: "1px", borderColor: "#ced4da", padding: 10, marginTop
    }
}


export default class Configurator extends React.Component {

    state = {
        waterChemistryManagement: WaterChemistryManagementOptions.none,
        installationType: installationTypeOptions.new_construction,
        devices: [],
        isModalVisible: false,
        isCompatibleAttendantProductsVisible: false,
        order: 0,
    }

    constructor(props) {
        super(props);

        this.handleClose = this.handleClose.bind(this);
        this.onEditDeviceSave = this.onEditDeviceSave.bind(this);
        this.addDevice = this.addDevice.bind(this);
        this.clearDevices = this.clearDevices.bind(this);
        this.showCompatibleAttendantProducts = this.showCompatibleAttendantProducts.bind(this);
        this.hideCompatibleAttendantProducts = this.hideCompatibleAttendantProducts.bind(this);
        this.loadStateFromLocalStorage = this.loadStateFromLocalStorage.bind(this);
        this.saveStateToLocalStorage = this.saveStateToLocalStorage.bind(this);
        this.clearState = this.clearState.bind(this);
        this.setAutoLoad = this.setAutoLoad.bind(this);
        this.setAutoSave = this.setAutoSave.bind(this);

        this.modalRef = React.createRef();

        if (context.userInfo?.admin) {
            this.localStorage = JSON.parse(window.localStorage.Configurator || "{}");
            if (this.localStorage.misc?.autoLoad) {
                this.state = this.localStorage.state || {};
            }
        }
    }

    setState(change, cb) {
        super.setState(change, () => {
            cb && cb();
            if (this.localStorage?.misc?.autoSave) {
                this.saveStateToLocalStorage();
            }
        })
    }
    
    handleClose() {
        this.setState({ isModalVisible: false }) 
    }

    addDevice() {
        this.modalRef?.current?.load({});
        this.setState({ isModalVisible: true })
    }

    clearDevices() {
        if (window.confirm(Locale.gettext("This will remove all the devices already added. Are you sure?"))) {
            this.setState({ devices: [] });
        }
    }

    onEditDeviceSave(record) {
        console.debug("[Configurator] onEditDeviceSave() record:", record);
        this.setState(prev => {
            const newState = {};
            if (record.uuid) {
                newState.devices = [ ...(prev.devices?.filter(item => item.uuid !== record.uuid)), record ];
            } else {
                // Toggle chem dropdown when adding chem related device.
                if (this.isDeviceChemistryRelated(record)) {
                    newState.waterChemistryManagement = WaterChemistryManagementOptions.sensing_and_or_dosing;
                }
                newState.order = ++prev.order;
                newState.devices = [ ...(prev.devices ? prev.devices : []), { ...record, uuid: newState.order } ];
            }
            newState.devices?.sort((a, b) => a.uuid > b.uuid ? 1 : -1);

            return newState;
        }); 
    }

    showCompatibleAttendantProducts() {
        if (!this.state.devices?.length) {
            alert(Locale.gettext("Please add some devices first."));
            return;
        }
        this.setState({ isCompatibleAttendantProductsVisible: true });
    }

    hideCompatibleAttendantProducts() {
        this.setState({ isCompatibleAttendantProductsVisible: false });
    }

    isDeviceChemistryRelated(device) {
        return (
            // Device uses chem realted port
            [
                PSLib.Config.PortTypeValues.Chemistry420,
                PSLib.Config.PortTypeValues.ChemistryORP,
                PSLib.Config.PortTypeValues.ChemistryPH,
            ].includes(device.portType)
            ||
            // Is it a chem related device
            [
                PSLib.Config.DeviceTypes.FLOWMETER,
                PSLib.Config.DeviceTypes.CHEMICALPUMP,
            ].includes(device.deviceType)
        );
    }

    getSku(platformCapabilitiesKey) {
        let sku = externalConfiguration.sku.platformCapabilitiesToSku[ platformCapabilitiesKey ];
        if (
            // sensing/dosing dropdown was set
            this.state.waterChemistryManagement === WaterChemistryManagementOptions.sensing_and_or_dosing
            ||
            // There are devices in the basket with chem related ports
            this.state.devices?.some(item => this.isDeviceChemistryRelated(item))
        ) {
            sku += "-CHEM";
        }
        return sku;
    }

    saveStateToLocalStorage() {
        this.setLocalStorate("state", this.state);
    }
    
    loadStateFromLocalStorage() {
        this.setState(JSON.parse(window.localStorage.Configurator || "{}")?.state);
    }

    setLocalStorate(prop, change) {
        const data = JSON.parse(window.localStorage.Configurator || "{}");
        if (!data[ prop ]) {
            data[ prop ] = {};
        }
        Object.assign(data[ prop ], change);
        this.localStorage = data;
        window.localStorage.Configurator = JSON.stringify(this.localStorage);
        this.forceUpdate();    
    }

    clearState() {
        if (confirm("Are you sure you want to clear state? (Wont affect saved state)")) {
            this.setState(prev => {
                const change = {};
                Object.keys(prev).forEach(item => change[ item ] = undefined);
                return change;
            });
        }
    }

    setAutoSave(evt) {
        this.setLocalStorate("misc", { autoSave: evt.target.checked });
    }

    setAutoLoad(evt) {
       this.setLocalStorate("misc", { autoLoad: evt.target.checked });
    }

    render() {
        console.debug("[Configurator] render() state:",this.state)

        const deviceTypes = [];
        this.state.devices?.forEach(item => {
            if (!deviceTypes.includes(item.deviceType)) {
                deviceTypes.push(item.deviceType);
            }
        });
        deviceTypes.sort((a,b) => a > b ? 1 : -1);
        
        const marginLeft = 30;

        let compatibleDevices = [];
        let compatibleDevicesError;
        if (this.state.devices?.length) {
            try {
                compatibleDevices = getCompatibleAttendants(this.state.devices);
            } catch (err) {
                console.error("[Configurator] render() getCompatibleAttendants() thown:", err, "devices:", this.state.devices);
                context.uploadLog("error", "[Configurator] render() getCompatibleAttendants() thown: " + err
                    + "\nstack: " + err.stack + "\ndevices: " + JSON.stringify(this.state.devices));
                compatibleDevicesError = err;
            }
        }
        
        const colTitle = 7;
        const colPortType = 2;

        return <div>
            <ConfiguratorAddDevice visible={ this.state.isModalVisible } onClose={ this.handleClose }
                onSave={ this.onEditDeviceSave }
                ref={ this.modalRef }
            />
            

            <Form.Group style={ { marginTop } }>
                <Form.Label>{ Locale.gettext("Water Chemistry Management") }:</Form.Label>
                <Form.Select value={ this.state.waterChemistryManagement } onChange={ evt => { 
                    this.setState({ waterChemistryManagement: evt.target.value })
                }}>
                    { Object.values(WaterChemistryManagementOptions).map(item => 
                        <option value={ item } key={ item }>{ getWaterChemistryManagementOptionsLocalised(item) }</option>
                    )}
                </Form.Select>
            </Form.Group>

            <Form.Group style={ { marginTop } }>
                <Form.Label>{ Locale.gettext("Installation Type") }:</Form.Label>
                <Form.Select value={ this.state.installationType } onChange={ evt => { 
                    this.setState({ installationType: evt.target.value })
                }}>
                    { Object.values(installationTypeOptions).map(item => 
                        <option value={ item } key={ item }>{ getInstallationTypeOptionsLocalised(item) }</option>
                    )}
                </Form.Select>
            </Form.Group>

            <fieldset style={ style.fieldset }>
                <legend style={ { width: "auto", fontSize: 16 } }>{ Locale.gettext("Equipment") }:</legend>

                { !deviceTypes.length && <div style={ { paddingBottom: 10 }} ><b>{ Locale.gettext("None") }</b></div> }
                { deviceTypes.length > 0 && 
                    <div style={ { marginLeft } }>
                        <Form.Group as={ Row } className="mb-2">
                        <Col xs={ colTitle }></Col>
                        <Col xs={ colPortType }><b>{ Locale.gettext("Port Type") }</b></Col>
                        <Col></Col>
                    </Form.Group>
                    </div>
                }
                
                { Object.values(PSLib.PoolDevice.PoolDeviceTypes)
                    .filter(item => deviceTypes.includes(item.default.DeviceType))
                    .map((itemDeviceTypes, idxItemDeviceTypes) => <div key={ itemDeviceTypes.default.DeviceType }>
                        <div><b>{ itemDeviceTypes.default.PickerTitle }</b></div>
                        <div style={ { marginLeft } }>
                            { this.state.devices?.filter(item => item.deviceType === itemDeviceTypes.default.DeviceType).map((itemDevice, idx) => {
                                const mockConfig = {
                                    DeviceType: itemDevice.deviceType,
                                    HardwareDevice: {
                                        PrimaryCatalogId: itemDevice.primaryCatalogId,
                                        SecondaryCatalogId: itemDevice.secondaryCatalogId,
                                        LightSettings: {
                                            CompatibleBrandId: itemDevice.CompatibleBrandId,
                                            ColorAndShowLibraryId: itemDevice.ColorAndShowLibraryId,
                                            // ColorAndShowSelectionMethodId: itemDevice.ColorAndShowSelectionMethodId,
                                        },
                                    }
                                };

                                const catalogEntry = PSLib.Catalog.getCatalogEntryByDeviceConfig(mockConfig);

                                const portTypes = PSLib.Config.getPrimaryPortPortTypes(mockConfig) 
                               
                                const deviceTitle = ((catalogEntry?.brand ?? "") + " " + (catalogEntry?.model ?? "") + " " + (catalogEntry?.description ?? "")).trim() || itemDevice?.deviceType;

                                return <Form.Group as={ Row } key={ itemDevice.uuid } style={ { marginTop: idx ? 10 : 0 } }>
                                    <Col xs={ colTitle }>
                                        <Form.Label column>{ deviceTitle }</Form.Label>
                                    </Col>
                                    <Col xs={ colPortType }>
                                        <Form.Select value={ itemDevice.portType } onChange={ evt => {
                                            this.setState(prev => ({
                                                devices: [
                                                    ...(prev.devices?.filter(item => item.uuid !== itemDevice.uuid)),
                                                    {
                                                        ...itemDevice,
                                                        portType: evt.target.value
                                                    }
                                                ]
                                            }))
                                        } }>
                                            <option></option>
                                            { portTypes?.map(item =>
                                                <option value={ item } key={ item }>{ PSLib.Config.getPortTypeLocalised(item) }</option>
                                            ) }
                                        </Form.Select>
                                    </Col>
                                    <Col>
                                        <Row>
                                            <Col>
                                                <Button type="submit" aria-label={ Locale.gettext("Edit") } onClick={ () => {
                                                    this.setState({ isModalVisible: true });
                                                    this.modalRef.current.load(itemDevice);
                                                } }>
                                                    <FaPen />
                                                </Button>
                                            </Col>
                                            <Col>
                                                <Button type="submit" aria-label={ Locale.gettext("Delete") } onClick={ () => {
                                                    if (window.confirm(Locale.gettext("Delete %s?", deviceTitle))) {
                                                        this.setState(prev => ({
                                                            devices: prev.devices?.filter(item => item.uuid !== itemDevice.uuid)
                                                        }));
                                                    }
                                                } }>
                                                    <FaTrash />
                                                </Button>
                                            </Col>
                                            <Col>
                                                <Button type="submit" aria-label={ Locale.gettext("Duplicate") } onClick={ () => {
                                                    const duplicate = JSON.parse(JSON.stringify(itemDevice));
                                                    delete duplicate.uuid;
                                                    this.onEditDeviceSave(duplicate);
                                                } }>
                                                    <FaClone />
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Form.Group> } ) }
                        </div>
                    </div> )
                }

                <Row style={ { paddingTop: 15 } }>
                <Col>
                    <Form.Control as="button" onClick={ this.clearDevices } className="btn btn-secondary">
                        { Locale.gettext("Clear All") }
                    </Form.Control>
                </Col>
                <Col>
                    <Form.Control as="button"  onClick={ this.addDevice } className="btn btn-primary">
                        { Locale.gettext("Add Device") }
                    </Form.Control>
                </Col>
                </Row>
            </fieldset>


        <fieldset style={ style.fieldset }>
            <legend style={ { width: "auto", fontSize: 16 } }>{ Locale.gettext("Compatible Attendant Models") }:</legend>
                {
                    this.state.devices?.length ?
                        compatibleDevicesError ?
                            <div><b>{ showError(compatibleDevicesError) }</b></div> : (
                            compatibleDevices?.length ?
                                compatibleDevices.map(item => <div key={ item }><b>{ this.getSku(item) }</b></div>) :
                                <div><b>{ Locale.gettext("The equipment list you selected may require multiple Attendant systems to control it.  Please reach out to our sales team to discuss this project in more detail at sales@poolside.tech or +1 (267) 810-0600") }</b></div>
                            )
                        :
                        <div><b>{ Locale.gettext("Please add some devices first.") }</b></div>
                }
        
        </fieldset>
                
        <fieldset style={ style.fieldset }>
            <legend style={ { width: "auto", fontSize: 16 } }>{ Locale.gettext("Recommended Accessories") }:</legend>
            { this.state.waterChemistryManagement === WaterChemistryManagementOptions.sensing_and_or_dosing && <div><b>
                ATT-CPK – Chemistry Probe Kit<br/>
                ATT-FLOW-CELL – Chemistry Flow Cell
            </b></div> }

            { this.state.installationType === installationTypeOptions.retrofit && <div><b>
                ATT-LACK – Legacy Automation Conversion Kit
            </b></div> }

        </fieldset>

        { !!context.userInfo?.admin && <fieldset style={ style.fieldset }>
            <legend>DEV</legend>
            
            <Row style={ { paddingTop: 15 } }>
                <Col>
                    <Form.Control as="button" onClick={ this.loadStateFromLocalStorage } className="btn btn-primary me-2">
                        { Locale.gettext("Load") }
                    </Form.Control>
                </Col>
                <Col>
                    <Form.Control as="button" onClick={ this.saveStateToLocalStorage } className="btn btn-primary me-2">
                        { Locale.gettext("Save") }
                    </Form.Control>
                </Col>
                <Col>
                    <Form.Control as="button" onClick={ this.clearState } className="btn btn-primary">
                        { Locale.gettext("Clear") }
                    </Form.Control>
                </Col>
            </Row>

            <Row style={ { paddingTop: 15 } }>
                <Col>
                    <Form.Check
                        checked={ !!this.localStorage?.misc?.autoLoad }
                        type="checkbox"
                        label="Auto load"
                        onChange={ this.setAutoLoad }
                    />
                </Col>
                <Col>
                    <Form.Check
                        checked={ !!this.localStorage?.misc?.autoSave }
                        type="checkbox"
                        label="Auto save"
                        onChange={ this.setAutoSave }
                    />
                </Col>
            </Row>
        </fieldset> }
        
            <div style={{ marginTop: "auto", textAlign: "center", padding: 10 }}>
                { Locale.gettext("Ensure every installation is perfectly matched!") }
            </div>
        
        </div>
    }
}